import {
  FontFamily,
  Line,
  PencilStroke,
  Play,
  Player,
  Position,
  ShapeType,
  Size,
  StrokeEnd,
  StrokeType,
  TextAnnotation,
} from "../api/types";

type PlayerRaw = Partial<Player> & { shape_type?: ShapeType; state_index?: 0 | 1 | 2 | 3 };
type LineRaw = Partial<Line> & {
  start_pos?: Position;
  end_pos?: Position;
  control_point?: Position;
  stroke_type?: StrokeType;
  stroke_end?: StrokeEnd;
  stroke_width?: number;
  attached_shape_id?: string;
  attached_shape_type?: ShapeType;
  attached_shape_size?: Size;
  drawn_from_id?: string;
  blocking_scheme?: string;
  combo_group_id?: string;
};
type TextAnnotationRaw = Partial<TextAnnotation> & {
  background_color?: string;
  font_size?: number;
  font_family?: FontFamily;
  is_bold?: boolean;
  is_italic?: boolean;
};
type PencilStrokeRaw = Partial<PencilStroke> & { stroke_width?: number };

type PlayRaw = Omit<Play, "shape_list" | "line_list" | "text_tag_list" | "pencil_stroke_list"> & {
  shape_list?: PlayerRaw[];
  line_list?: LineRaw[];
  text_tag_list?: TextAnnotationRaw[];
  pencil_stroke_list?: PencilStrokeRaw[];
};

// Defensive default: malformed API payloads missing position land at origin rather than crashing render.
function normalizePosition(pos?: Position): Position {
  return { x: pos?.x ?? 0, y: pos?.y ?? 0 };
}

function normalizeSize(size?: Size): Size {
  return { width: size?.width ?? 0, height: size?.height ?? 0 };
}

function normalizePlayer(player: PlayerRaw): Player {
  return {
    id: player.id ?? "",
    position: normalizePosition(player.position),
    shapeType: player.shapeType ?? player.shape_type ?? "receiverOval",
    color: player.color ?? "#000000",
    text: player.text ?? "",
    stateIndex: (player.stateIndex ?? player.state_index ?? 0) as 0 | 1 | 2 | 3,
  };
}

function normalizeLine(line: LineRaw): Line {
  const attachedShapeSize = line.attachedShapeSize ?? line.attached_shape_size;
  return {
    id: line.id ?? "",
    startPos: normalizePosition(line.startPos ?? line.start_pos),
    endPos: normalizePosition(line.endPos ?? line.end_pos),
    controlPoint: normalizePosition(line.controlPoint ?? line.control_point),
    color: line.color ?? "#000000",
    strokeType: line.strokeType ?? line.stroke_type ?? "solid",
    strokeEnd: line.strokeEnd ?? line.stroke_end ?? "straight",
    strokeWidth: line.strokeWidth ?? line.stroke_width ?? 1,
    attachedShapeId: line.attachedShapeId ?? line.attached_shape_id,
    attachedShapeType: line.attachedShapeType ?? line.attached_shape_type,
    attachedShapeSize: attachedShapeSize ? normalizeSize(attachedShapeSize) : undefined,
    drawnFromId: line.drawnFromId ?? line.drawn_from_id,
    blockingScheme: line.blockingScheme ?? line.blocking_scheme,
    comboGroupId: line.comboGroupId ?? line.combo_group_id,
  };
}

function normalizeTextAnnotation(annotation: TextAnnotationRaw): TextAnnotation {
  return {
    id: annotation.id ?? "",
    position: normalizePosition(annotation.position),
    text: annotation.text ?? "",
    color: annotation.color ?? "#000000",
    backgroundColor: annotation.backgroundColor ?? annotation.background_color,
    fontSize: annotation.fontSize ?? annotation.font_size ?? 16,
    fontFamily: annotation.fontFamily ?? annotation.font_family ?? "interBold",
    isBold: annotation.isBold ?? annotation.is_bold ?? false,
    isItalic: annotation.isItalic ?? annotation.is_italic ?? false,
  };
}

function normalizePencilStroke(stroke: PencilStrokeRaw): PencilStroke {
  return {
    id: stroke.id ?? "",
    points: (stroke.points ?? []).map(normalizePosition),
    color: stroke.color ?? "#000000",
    strokeWidth: stroke.strokeWidth ?? stroke.stroke_width ?? 1,
    offset: normalizeSize(stroke.offset),
  };
}

export function normalizePlay(play: Play | PlayRaw): Play {
  return {
    ...play,
    width: play.width ?? 0,
    height: play.height ?? 0,
    shape_list: (play.shape_list ?? []).map(normalizePlayer),
    line_list: (play.line_list ?? []).map(normalizeLine),
    text_tag_list: (play.text_tag_list ?? []).map(normalizeTextAnnotation),
    pencil_stroke_list: (play.pencil_stroke_list ?? []).map(normalizePencilStroke),
  };
}
